// Royal Mail PAF address picking: the pure half (no DOM), pinned by
// address-pick.test.mjs. The DOM half is components/address-pick, built as
// its own small script (client/packs/address-pick.js) that the server adds
// only while ADDRESS_LOOKUP_PROVIDER is on (docs/ADDRESS_LOOKUP.md), so the
// main bundle (gwmc.js, 250 KB budget) carries none of it.
//
// Two users:
//   - Book & pay's one Car location box (19 Oct 2026, lib/car-location.js):
//     a street typed gets typeahead suggestions (free), a whole postcode its
//     addresses; a pick is handed to the page.
//   - the admin manual-order form's postcode and house number boxes.
import { isFullPostcode, mayBeInCongestionZone, normalisePostcode } from './postcode.js'
import { outsideUsualArea, postcodeOutward } from './postcode-check.js'

// The admin form's postcode box and the house number box it fills
// (ActiveAdmin, `order_<attribute>`). Book & pay's pair went with the Car
// location box.
export const ADDRESS_PICK_PAIRS = [
  { postcode: 'order_postcode', house: 'order_address' }
]

export const pairFor = id => ADDRESS_PICK_PAIRS.find(p => p.postcode === id || p.house === id) || null

export const addressesUrl = postcode => `/api/v2/addresses?postcode=${encodeURIComponent(normalisePostcode(postcode))}`
export const suggestUrl = text => `/api/v2/addresses/suggest?q=${encodeURIComponent(String(text).trim().replace(/\s+/g, ' '))}`
export const resolveUrl = id => `/api/v2/addresses/${encodeURIComponent(id)}`

// What one row of a postcode's list says: the address line and the town.
export const labelFor = entry => [entry && entry.line, entry && entry.town].filter(Boolean).join(', ')

// What one tap writes on the admin form: the postcode box gets PAF's
// postcode, the house number box the address line (the server adds the
// postcode on the end, and matches the line to store the coordinates,
// Order::AddressCoordinates).
export const pickValues = entry => ({ postcode: entry.postcode, house: entry.line })

// ---------------------------------------------------------------------------
// Book & pay's Car location box.

export const CAR_LOCATION_ID = 'book-carLocation'
export const CAR_LOCATION_LIST_ID = 'book-carLocation-list'
export const SUGGEST_MIN = 3
export const DEBOUNCE_MS = 250

// A whole postcode at the start or the end of what is typed, with whatever
// else is there: "SW6 1RE 45", "45 SW6 1RE", "sw61re flat 3".
const POSTCODE_IN = /^(?:(.*?)[\s,]+)?([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})(?:[\s,]+(.*))?$/i

// { postcode, filter } when the box holds a whole postcode (filter: the
// house number or name typed with it, '' for none), else null.
export const postcodeQuery = text => {
  const t = String(text || '').trim()
  const m = t.match(POSTCODE_IN)
  if (!m) return null
  const filter = [m[1], m[3]].filter(Boolean).join(' ').replace(/[\s,]+/g, ' ').trim()
  return { postcode: m[2], filter }
}

// What to ask for what is in the box: 'postcode' (the box holds a whole
// postcode, with or without a house number: its addresses), 'autocomplete'
// (3 characters or more: the typeahead) or null (too short).
export const queryKind = text => {
  if (postcodeQuery(text)) return 'postcode'
  return String(text || '').trim().length >= SUGGEST_MIN ? 'autocomplete' : null
}

// A long postcode list (both payers of 21 Oct scrolled to rows 14 and 18)
// says the list can be shortened by typing the house number after it.
export const FILTER_HINT_MIN = 6

const words = text => String(text || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean)

// One word typed against one address's words: 3 the same word ("45" is
// 45, not 145), 2 the start of one ("4" while typing 45, "rose" for Rose
// Cottage), 1 inside one (a name word of 3 letters or more); 0 none.
const wordScore = (q, entryWords) => {
  let best = 0
  for (const w of entryWords) {
    if (w === q) return 3
    if (w.startsWith(q)) best = Math.max(best, 2)
    else if (q.length >= 3 && !/^\d+$/.test(q) && w.includes(q)) best = Math.max(best, 1)
  }
  return best
}

// The postcode's addresses narrowed to what was typed with it, best first:
// every word must match (a word that matches no address at all, "rd" for
// Road, is let go, but never a number), then the most exact, then the line
// that starts with it (45 Elm Road before Flat 45), then PAF's order.
// Returns { entries, matched } (matched false: nothing fits, all of them).
export const filterAddresses = (entries, filter) => {
  const list = Array.isArray(entries) ? entries : []
  const typed = words(filter)
  if (!typed.length) return { entries: list, matched: true }
  const rows = list.map((entry, i) => ({ entry, i, words: words(`${labelFor(entry)} ${entry.street || ''}`), first: words(entry.line)[0] }))
  const needed = typed.filter(q => /^\d+$/.test(q) || rows.some(r => wordScore(q, r.words) > 0))
  if (!needed.length) return { entries: list, matched: false }
  const scored = rows
    .map(r => {
      const each = needed.map(q => wordScore(q, r.words))
      return { ...r, ok: each.every(n => n > 0), score: each.reduce((a, b) => a + b, 0) + (r.first && r.first.startsWith(needed[0]) ? 0.5 : 0) }
    })
    .filter(r => r.ok)
    .sort((a, b) => b.score - a.score || a.i - b.i)
  return scored.length ? { entries: scored.map(r => r.entry), matched: true } : { entries: list, matched: false }
}

// car_location_filtered's properties: how many of the postcode's addresses
// are left, of how many, and whether a number was typed. Never the text.
export const filteredProps = ({ count, total, filter }) => ({ count, total, has_number: /\d/.test(String(filter || '')) })

// The query's length for car_location_suggestions_shown, never the text.
export const lengthBucket = n => (n <= 5 ? '3-5' : n <= 10 ? '6-10' : n <= 20 ? '11-20' : '21+')

// car_location_picked's properties: where the pick came from, its place in
// the list (1 first), whether PAF has a UPRN for it (the coordinates come
// only then), and the coverage and Congestion Charge checks the postcode box
// makes. No address, no postcode.
export const pickedProps = ({ entry, source, position }) => ({
  source,
  position,
  has_uprn: entry.lat != null && entry.lng != null,
  covered: !outsideUsualArea(entry.postcode),
  cz: mayBeInCongestionZone(entry.postcode)
})

// car_location_not_covered: the outward code only ("CF10").
export const notCoveredProps = entry => ({ outward: postcodeOutward(entry.postcode) })

// url -> the JSON body, or { failed: status } (0 for no answer). Each URL
// is asked once per page; a failure is not remembered, so a later try can
// succeed.
export const makeJsonFetcher = fetchFn => {
  const known = new Map()
  return url => {
    if (typeof fetchFn !== 'function') return Promise.resolve({ failed: 0 })
    if (!known.has(url)) {
      const asked = fetchFn(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(res => (res && res.ok ? res.json() : { failed: (res && res.status) || 0 }))
        .catch(() => ({ failed: 0 }))
        .then(body => {
          if (!body || body.failed !== undefined) known.delete(url)
          return body || { failed: 0 }
        })
      known.set(url, asked)
    }
    return known.get(url)
  }
}

// The rows for what is in the box: { kind, items: [{ label, id?, entry? }] }
// with [] for nothing found, or { kind, failed } when the lookup did not
// answer (switched off, the limit, the provider down). null: nothing to ask.
// A postcode typed with a house number or name ("SW6 1RE 45") lists that
// postcode's addresses narrowed to it (filterAddresses; asked once per
// postcode, the narrowing is in the browser), and says so: `filter` (what
// was typed with the postcode), `total` (the whole list) and `matched`
// (false: nothing fits, so the whole list).
export const makeCarLocationLookup = fetchFn => {
  const getJson = makeJsonFetcher(fetchFn)
  return text => {
    const kind = queryKind(text)
    if (!kind) return Promise.resolve(null)
    const pc = kind === 'postcode' ? postcodeQuery(text) : null
    const url = pc ? addressesUrl(pc.postcode) : suggestUrl(text)
    return getJson(url).then(body => {
      if (!Array.isArray(body)) return { kind, failed: true }
      if (!pc) return { kind, items: body.filter(s => s && s.id && s.suggestion).map(s => ({ label: s.suggestion, id: s.id })) }
      const all = body.filter(e => e && e.line)
      const toItems = list => list.map(entry => ({ label: labelFor(entry), entry }))
      if (!pc.filter) return { kind, items: toItems(all) }
      const { entries, matched } = filterAddresses(all, pc.filter)
      return { kind, items: toItems(entries), filter: pc.filter, total: all.length, matched }
    })
  }
}

// postcode -> the list, or null when there is nothing to offer (not a whole
// postcode, the switch off, the provider down, the limit hit). The admin
// form's picker.
export const makeAddressFetcher = fetchFn => {
  const getJson = makeJsonFetcher(fetchFn)
  return postcode => {
    if (!isFullPostcode(postcode) || typeof fetchFn !== 'function') return Promise.resolve(null)
    return getJson(addressesUrl(postcode)).then(list => (Array.isArray(list) ? list.filter(e => e && e.line) : null))
  }
}
